import { useState } from 'react'
import { Link } from 'react-router-dom'
import { tenantConfig } from '../config/tenant'
import Nav from '../components/Nav'
import Seo from '../components/Seo'
import Footer from '../components/Footer'
import LeadCaptureModal from '../components/LeadCaptureModal'
import RatesStrip from '../components/RatesStrip'
import LenderWall from '../components/LenderWall'
import GoogleReviews from '../components/GoogleReviews'
import RateCarousel from '../components/RateCarousel'
import StructuredData from '../components/StructuredData'
import { BROKERS } from '../data/team'

// Large lazy-loaded thumbnail path for a team headshot used in the home teaser.
// The ?v suffix busts browser/CDN cache when the thumbnail is re-cropped (the
// filename is stable and public/ assets are not content-hashed). Bump on recrop.
const TEAM_THUMB_V = '2'
const teamThumb = (img: string) =>
  img.replace('/assets/team/', '/assets/team/thumbs-lg/').replace(/\.(png|jpe?g)$/i, '.jpg') +
  `?v=${TEAM_THUMB_V}`

// The four faces shown in the home teaser (Chelston's selection); the full team is on /about.
const TEASER_NAMES = ['Chelston Alvares', 'Brodie Callanan', 'Brett Callanan', 'Kristina Roycroft']

const SERVICES: { title: string; body: string; to?: string }[] = [
  {
    title: 'First home buyers',
    body: 'Grants, stamp duty concessions, deposit strategy. We handle the paperwork so you focus on inspections.',
  },
  {
    title: 'Refinance',
    body: 'Compare your existing rate against the market. See how much you could save, then we handle the switch.',
    to: '/tools/savings',
  },
  {
    title: 'Investment loans',
    body: 'Structure, tax deductibility, offset accounts. Speak with someone who&apos;s seen a hundred portfolios.',
  },
  {
    title: 'Construction',
    body: 'Progressive drawdowns, builder due-diligence, valuations. Guiding first-time builders end-to-end.',
  },
]

export default function HomePage() {
  const [leadOpen, setLeadOpen] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const teaserMembers = TEASER_NAMES.map((n) => BROKERS.find((b) => b.name === n)).filter(
    (b): b is (typeof BROKERS)[number] => !!b && !!b.image,
  )

  return (
    <>
      {/* Brand-first so the tab shows "Greystone Finance …" instead of truncating it off
          the end (Chelston/#34). Includes the tenant name, so <Seo> won't re-append it. */}
      <Seo
        title={`${tenantConfig.name} · Mortgage Broking & Lending Advisory`}
        path="/"
        description={`${tenantConfig.name} compares ${tenantConfig.lenderCount}+ lenders to find you the right home loan, refinance or investment loan. Personal mortgage broking across Australia, from first home buyers to business owners.`}
      />
      <StructuredData />
      <Nav />
      <main id="main" className="pt-36">
        {/* Curated rate carousel — only the lenders selected in /dataentry (hidden if none) */}
        <RateCarousel />

        {/* Hero */}
        <section className="relative overflow-hidden">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse 600px 400px at 85% 10%, rgba(226,255,96,0.18), transparent 60%), radial-gradient(ellipse 700px 500px at 0% 100%, rgba(226,255,96,0.10), transparent 60%)',
            }}
            aria-hidden="true"
          />
          <div className="relative max-w-7xl mx-auto px-6 py-16 grid gap-12 lg:grid-cols-[1.4fr_1fr] items-start">
            <div>
              <div className="inline-flex items-center gap-2 border border-[#E8E8E2] rounded-full px-3 py-1 text-xs font-medium text-gs-text-soft mb-6">
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: tenantConfig.accentColor }}
                />
                Mortgage Broking &amp; Lending Advisory
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-ink font-semibold mb-6 leading-tight">
                <span className="block">The right home loan.</span>
                <span className="block">The right strategy.</span>
                <span className="block">Your financial freedom.</span>
              </h1>
              <p className="text-lg text-gs-text-soft mb-8 max-w-xl">
                {tenantConfig.name} works across Australia&apos;s major lenders to find you the right
                rate, structure, strategy, and someone who&apos;ll keep you in the loop throughout the
                entire process.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setLeadOpen(true)}
                  className="px-6 py-3.5 rounded-lg font-semibold text-sm"
                  style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
                >
                  Talk to a broker
                </button>
                <Link
                  to="/tools"
                  className="px-6 py-3.5 rounded-lg font-semibold text-sm border border-[#D8D8D0] hover:bg-white transition-colors inline-flex items-center gap-1.5"
                >
                  See your numbers in 30 seconds <span aria-hidden="true">→</span>
                </Link>
              </div>
              {/* Trust stats moved into the bold "by the numbers" band directly below
                  the hero (Chelston feedback #33) — keeps the hero clean and lets the
                  proof points pop on a dark ground instead of the faint grey row. */}
            </div>

            <div className="relative">
              <RatesStrip />
            </div>
          </div>
        </section>

        {/* Team teaser over the "by the numbers" band — "the people behind your loan" with a
            Meet-the-team link to the About team. Four large lazy-loaded thumbnails (~24KB each)
            below the fold, so no impact on initial load / LCP. */}
        <section className="max-w-7xl mx-auto px-6 mt-2 mb-8">
          <div className="flex items-end justify-between gap-4 mb-5">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted mb-1">The team</div>
              <h2 className="font-heading text-2xl md:text-3xl text-ink">Meet Your Finance Team</h2>
            </div>
            <Link
              to="/about#team"
              className="shrink-0 text-sm font-semibold text-ink inline-flex items-center gap-1.5 hover:gap-2 transition-all"
            >
              Meet the team <span aria-hidden="true">→</span>
            </Link>
          </div>

          {/* Uniform photo strip resting flush on the dark band. Each photo is an equal
              fixed-ratio cell with object-cover + object-top, so every subject is framed
              identically. The bottom of the strip fades into the band colour so the white
              studio backgrounds (which always leave pale edges beside the torso at the very
              bottom of a headshot) dissolve seamlessly — no hard line at the junction,
              consistent across every photo regardless of its background or clothing. */}
          <div className="relative max-w-3xl mx-auto -mb-px overflow-hidden rounded-t-2xl">
            <div className="grid grid-cols-4">
              {teaserMembers.map((b) => (
                <Link key={b.name} to="/about#team" className="block aspect-[4/5] bg-white">
                  <img
                    src={teamThumb(b.image as string)}
                    alt={b.name}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover object-top"
                  />
                </Link>
              ))}
            </div>
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-0 h-16"
              style={{ background: `linear-gradient(to bottom, transparent, ${tenantConfig.primaryColor})` }}
            />
          </div>

          <div
            className="relative overflow-hidden rounded-3xl px-8 pt-8 pb-10 md:px-14"
            style={{ background: tenantConfig.primaryColor }}
          >
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'radial-gradient(ellipse 520px 320px at 100% 0%, rgba(226,255,96,0.20), transparent 60%)',
              }}
              aria-hidden="true"
            />
            {/* Names, aligned under the photos */}
            <div className="relative grid grid-cols-4 gap-2 md:gap-6 max-w-3xl mx-auto text-center mb-8">
              {teaserMembers.map((b) => (
                <div key={b.name} className="text-[11px] md:text-sm font-medium text-white/90 leading-tight">{b.name}</div>
              ))}
            </div>
            <div className="relative border-t border-white/10 pt-8 grid gap-8 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-white/10">
              {/* Google reviews */}
              <a href="#reviews" className="group block sm:pr-10 lg:pr-14" aria-label="Read our Google reviews">
                {/* Fixed-height slot so 105 shares a top line with 380+/$300M+ (which have no stars). */}
                <div className="flex items-center h-5 mb-3 text-amber-400" aria-label="Rated 5 stars on Google">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <svg key={i} className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path d="M9.05 2.93c.3-.92 1.6-.92 1.9 0l1.36 4.18a1 1 0 00.95.69h4.4c.97 0 1.37 1.24.59 1.81l-3.56 2.59a1 1 0 00-.36 1.12l1.36 4.18c.3.92-.76 1.68-1.54 1.11l-3.56-2.59a1 1 0 00-1.18 0l-3.56 2.59c-.78.57-1.84-.19-1.54-1.11l1.36-4.18a1 1 0 00-.36-1.12L2.4 9.6c-.78-.57-.38-1.81.59-1.81h4.4a1 1 0 00.95-.69L9.05 2.93z" />
                    </svg>
                  ))}
                </div>
                <div className="font-heading text-4xl md:text-5xl font-bold leading-none" style={{ color: tenantConfig.accentColor }}>
                  105
                </div>
                <div className="mt-2 text-sm text-white/60 group-hover:text-white/85 transition-colors">
                  Google reviews · <span className="underline underline-offset-2">Read them →</span>
                </div>
              </a>
              {/* Clients under management */}
              <div className="sm:px-10 lg:px-14">
                <div className="h-5 mb-3" aria-hidden="true" />
                <div className="font-heading text-4xl md:text-5xl font-bold leading-none" style={{ color: tenantConfig.accentColor }}>
                  380+
                </div>
                <div className="mt-2 text-sm text-white/60">Clients under management</div>
              </div>
              {/* Settled & lending */}
              <div className="sm:pl-10 lg:pl-14">
                <div className="h-5 mb-3" aria-hidden="true" />
                <div className="font-heading text-4xl md:text-5xl font-bold leading-none" style={{ color: tenantConfig.accentColor }}>
                  $300M+
                </div>
                <div className="mt-2 text-sm text-white/60">Settled &amp; lending under management</div>
              </div>
            </div>
          </div>
        </section>

        {/* Free property report — captures the address + contact as a lead; the Greystone
            team generates the CoreLogic report and sends it to the customer manually. */}
        <section className="max-w-7xl mx-auto px-6 mb-10">
          <div
            className="rounded-3xl border border-[#E8E8E2] p-8 md:p-12 grid gap-6 md:grid-cols-[1.5fr_1fr] md:items-center"
            style={{ background: '#F4FBD9' }}
          >
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted mb-2">Free for buyers &amp; owners</div>
              <h2 className="font-heading text-2xl md:text-3xl text-ink font-semibold mb-3">Get your free property report.</h2>
              <p className="text-gs-text-soft max-w-xl">
                Thinking of buying, selling or refinancing? Enter an address and we will prepare a free
                property report, recent comparable sales, an estimated value range and suburb insights, and
                send it straight to you. No cost, no obligation.
              </p>
            </div>
            <div className="md:text-right">
              <button
                onClick={() => setReportOpen(true)}
                className="px-6 py-3.5 rounded-xl font-semibold text-sm w-full md:w-auto"
                style={{ background: tenantConfig.primaryColor, color: tenantConfig.accentColor }}
              >
                Get your free property report
              </button>
            </div>
          </div>
        </section>

        {/* Lender panel — "we compare N+ lenders" logo wall */}
        <LenderWall />

        {/* Live Google reviews carousel (Featurable) */}
        <GoogleReviews />

        {/* Services grid */}
        <section className="max-w-7xl mx-auto px-6 py-20">
          <div className="mb-10">
            <div className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted mb-2">What we do</div>
            <h2 className="font-heading text-3xl md:text-4xl text-ink">Every scenario, one broker.</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {SERVICES.map((s) =>
              s.to ? (
                <Link
                  key={s.title}
                  to={s.to}
                  className="p-6 bg-white border border-[#E8E8E2] rounded-2xl card-hover block group"
                >
                  <h3 className="font-heading font-semibold text-ink text-lg mb-2">{s.title}</h3>
                  <p className="text-sm text-gs-text-soft" dangerouslySetInnerHTML={{ __html: s.body }} />
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-ink group-hover:gap-2 transition-all">
                    How much could you save? <span aria-hidden="true">→</span>
                  </span>
                </Link>
              ) : (
                <div
                  key={s.title}
                  className="p-6 bg-white border border-[#E8E8E2] rounded-2xl card-hover"
                >
                  <h3 className="font-heading font-semibold text-ink text-lg mb-2">{s.title}</h3>
                  <p className="text-sm text-gs-text-soft" dangerouslySetInnerHTML={{ __html: s.body }} />
                </div>
              ),
            )}
          </div>
        </section>

        {/* How we'll work together — the process (restored for trust + SEO, per Chelston's feedback) */}
        <section id="process" className="max-w-7xl mx-auto px-6 py-20" style={{ scrollMarginTop: '100px' }}>
          <div className="mb-10">
            <div className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted mb-2">How it works</div>
            <h2 className="font-heading text-3xl md:text-4xl text-ink">How we&apos;ll work together.</h2>
            <p className="text-gs-text-soft mt-3 max-w-2xl text-lg">
              From understanding your goals to settlement and beyond, here&apos;s what working with us looks like.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              { n: '01', title: 'Understand your goals', body: 'We take the time to understand your situation, your goals and what a good outcome looks like for you.' },
              { n: '02', title: 'Compare your options', body: 'We research across 60+ lenders and bring you a shortlist of loan options, with clear guidance on the trade-offs.' },
              { n: '03', title: 'We get it sorted', body: "Once you've chosen, we do the legwork (paperwork, lodgement, approval and settlement) and keep you updated at every step." },
              { n: '04', title: 'Ongoing support & savings', body: 'We stay in touch as your life changes, and keep an eye out for opportunities to save you money down the track.' },
            ].map((step) => (
              <div key={step.n} className="p-6 bg-white border border-[#E8E8E2] rounded-2xl card-hover">
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center font-heading font-semibold text-ink mb-4"
                  style={{ background: '#F4FBD9' }}
                >
                  {step.n}
                </div>
                <h3 className="font-heading font-semibold text-ink text-lg mb-2">{step.title}</h3>
                <p className="text-sm text-gs-text-soft leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
          <p className="text-gs-text-soft mt-8">
            Want a head start?{' '}
            <Link to="/tools" className="text-ink font-medium underline underline-offset-2">Try the borrowing calculator</Link>{' '}
            or{' '}
            <button type="button" onClick={() => setLeadOpen(true)} className="text-ink font-medium underline underline-offset-2">book a call with a broker</button>.
          </p>
        </section>

        {/* CTA band */}
        <section className="max-w-7xl mx-auto px-6 pb-20">
          <div
            className="rounded-3xl p-10 md:p-16 grid gap-8 md:grid-cols-[1.5fr_1fr] items-center"
            style={{ background: tenantConfig.primaryColor }}
          >
            <div>
              <h2 className="font-heading text-3xl md:text-4xl mb-4" style={{ color: tenantConfig.accentColor }}>
                Ready when you are.
              </h2>
              <p className="text-white/80 text-lg mb-2">
                No obligation. No lender-shopping fatigue. Just one conversation to see if we&apos;re the right fit.
              </p>
            </div>
            <button
              onClick={() => setLeadOpen(true)}
              className="px-6 py-4 rounded-lg font-semibold justify-self-start md:justify-self-end"
              style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
            >
              Start a conversation
            </button>
          </div>
        </section>
      </main>
      <Footer />

      <LeadCaptureModal open={leadOpen} onClose={() => setLeadOpen(false)} source="website" />

      <LeadCaptureModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        source="property_report"
        addressField
        addressLabel="Property address for the report"
        heading="Get your free property report"
        blurb="Enter the address and your details. A broker will prepare your property report and send it to you, no cost, no obligation."
        submitLabel="Send me my free report"
        context={{ enquiry_type: 'Free property report' }}
      />
    </>
  )
}
